// Renders a list of child blocks inside a container slot (a tab body, a
// column, the children of a Container/Card block, etc.). Each child is
// rendered with the same `<BlockNode>` used at the section level so the
// editing experience is identical regardless of nesting depth.
//
// Cross-cutting state — the page model (for unique id generation), the
// current selection, runtime input values, and the project name — is
// pulled from `NestedBlockContext` rather than threaded through every
// container canvas, which keeps `TabsCanvas`/`ContainerCanvas` etc. from
// having to forward unrelated props.
//
// Cross-slot drag-and-drop is intentionally *not* wired here in Phase 1.
// Nested blocks can be reordered with the up/down arrows and removed
// with the trash icon; section-level dragging still works at the top
// level.

import { createContext, useContext, useState } from 'react'
import type { CSSProperties } from 'react'
import { Plus, ArrowUp, ArrowDown, Trash2 } from 'lucide-react'
import type { Block, BlockType, PageInput, PageModel, Selection } from './types'
import BlockNode from './BlockNode'
import { BLOCK_OPTIONS, makeBlock } from './blocks'
import { nextBlockIdForType } from './pageOps'

// ─── Context ───────────────────────────────────────────────────────────

export interface NestedBlockContextValue {
  page: PageModel
  selection: Selection
  onSelectBlock: (blockId: string) => void
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  project: string
  onInputValueChange: (name: string, value: string) => void
}

export const NestedBlockContext = createContext<NestedBlockContextValue | null>(null)

// ─── Component ─────────────────────────────────────────────────────────

interface Props {
  blocks: Block[]
  onChange: (next: Block[]) => void
  emptyLabel?: string                     // shown when blocks is empty; undefined = no placeholder
  addLabel?: string                       // override "Add block"
}

export default function ChildBlockList({ blocks, onChange, emptyLabel, addLabel }: Props) {
  const ctx = useContext(NestedBlockContext)
  const [showPicker, setShowPicker] = useState(false)

  if (!ctx) {
    return (
      <div style={{ padding: 8, color: '#dc2626', fontSize: 13 }}>
        ChildBlockList: missing NestedBlockContext provider.
      </div>
    )
  }

  const {
    page, selection, onSelectBlock,
    inputs, inputValues, project, onInputValueChange,
  } = ctx

  const updateAt = (i: number, next: Block) => {
    const arr = [...blocks]
    arr[i] = next
    onChange(arr)
  }
  const removeAt = (i: number) => {
    onChange(blocks.filter((_, k) => k !== i))
  }
  const moveAt = (i: number, delta: -1 | 1) => {
    const to = i + delta
    if (to < 0 || to >= blocks.length) return
    const arr = [...blocks]
    ;[arr[i], arr[to]] = [arr[to], arr[i]]
    onChange(arr)
  }
  const addBlock = (type: BlockType) => {
    const id = nextBlockIdForType(page, type)
    const block = makeBlock(type, id, { pageName: page.obj_name })
    onChange([...blocks, block])
    onSelectBlock(block.id)
    setShowPicker(false)
  }
  const insertAfter = (i: number, type: BlockType = 'paragraph') => {
    const id = nextBlockIdForType(page, type)
    const block = makeBlock(type, id, { pageName: page.obj_name })
    const arr = [...blocks]
    arr.splice(i + 1, 0, block)
    onChange(arr)
    onSelectBlock(block.id)
  }

  const noop = () => {}
  // Group block options by category for a friendlier picker.
  const grouped = BLOCK_OPTIONS.reduce<Record<string, typeof BLOCK_OPTIONS>>((acc, opt) => {
    (acc[opt.category] = acc[opt.category] || []).push(opt)
    return acc
  }, {})

  return (
    <div className="pg-child-block-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {blocks.length === 0 && emptyLabel !== undefined && emptyLabel !== '' && (
        <div style={{
          padding: '12px 14px',
          border: '1px dashed var(--pg-border)',
          borderRadius: 6,
          color: 'var(--pg-text-secondary)',
          fontSize: '0.8825rem',
          textAlign: 'center',
        }}>
          {emptyLabel}
        </div>
      )}
      {blocks.map((b, i) => {
        const isSelected = selection.type === 'block' && selection.blockId === b.id
        return (
          <div
            key={b.id}
            className="pg-nested-block"
            // Stop propagation so clicking a nested block selects only
            // the nested block, not its parent container (the parent's
            // BlockNode wrapper has its own onClick that would otherwise
            // fire after the inner one and steal the selection).
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              border: isSelected ? '1px solid var(--pg-accent)' : '1px solid transparent',
              borderRadius: 6,
              padding: 6,
              background: isSelected ? 'rgba(76,141,255,0.04)' : 'transparent',
              transition: 'border-color 0.12s, background 0.12s',
            }}
          >
            <div
              className="pg-nested-controls"
              style={{
                position: 'absolute',
                top: 4, right: 4,
                display: 'flex',
                gap: 2,
                zIndex: 2,
                opacity: isSelected ? 1 : 0,
                transition: 'opacity 0.12s',
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                title="Move up"
                disabled={i === 0}
                onClick={(e) => { e.preventDefault(); moveAt(i, -1) }}
                style={{ ...ctrlBtnStyle, opacity: i === 0 ? 0.4 : 1 }}
              >
                <ArrowUp size={12} />
              </button>
              <button
                type="button"
                title="Move down"
                disabled={i === blocks.length - 1}
                onClick={(e) => { e.preventDefault(); moveAt(i, 1) }}
                style={{ ...ctrlBtnStyle, opacity: i === blocks.length - 1 ? 0.4 : 1 }}
              >
                <ArrowDown size={12} />
              </button>
              <button
                type="button"
                title="Delete block"
                onClick={(e) => { e.preventDefault(); removeAt(i) }}
                style={{ ...ctrlBtnStyle, color: '#dc2626' }}
              >
                <Trash2 size={12} />
              </button>
            </div>
            <BlockNode
              block={b}
              index={i}
              selected={isSelected}
              inputs={inputs}
              inputValues={inputValues}
              project={project}
              onSelect={() => onSelectBlock(b.id)}
              onChange={(next) => updateAt(i, next)}
              onTextKeyDown={noop}
              onInsertAfter={() => insertAfter(i, 'paragraph')}
              onTransform={noop}
              onDragStart={noop}
              onDragOver={noop}
              onDrop={noop}
              onDragEnd={noop}
              canDrag={false}
              onInputValueChange={onInputValueChange}
              showDropIndicator={null}
            />
          </div>
        )
      })}
      {/* CSS hover handles for nested controls — show on row hover */}
      <style>{`
        .pg-nested-block:hover .pg-nested-controls { opacity: 1 !important; }
      `}</style>
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowPicker(p => !p) }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 10px', fontSize: '0.8425rem',
            background: 'var(--pg-surface)',
            border: '1px dashed var(--pg-border)',
            borderRadius: 6,
            color: 'var(--pg-text-secondary)',
            cursor: 'pointer',
          }}
        >
          <Plus size={12} /> {addLabel || 'Add block'}
        </button>
        {showPicker && (
          <>
            <div
              onClick={() => setShowPicker(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 19, background: 'transparent' }}
            />
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: '100%', left: 0,
                marginTop: 4,
                background: 'var(--pg-surface)',
                border: '1px solid var(--pg-border)',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                zIndex: 20,
                minWidth: 240,
                maxHeight: 360,
                overflowY: 'auto',
                padding: 6,
              }}
            >
              {Object.entries(grouped).map(([cat, opts]) => (
                <div key={cat} style={{ marginBottom: 4 }}>
                  <div style={{
                    padding: '4px 8px', fontSize: '0.8125rem', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    color: 'var(--pg-text-secondary)',
                  }}>{cat}</div>
                  {opts.map(opt => (
                    <button
                      key={opt.type}
                      type="button"
                      onClick={(e) => { e.preventDefault(); addBlock(opt.type) }}
                      style={pickerItemStyle}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--pg-bg)' }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                    >
                      <span style={{ width: 18, textAlign: 'center', color: 'var(--pg-text-secondary)' }}>{opt.icon}</span>
                      <span style={{ flex: 1, fontWeight: 500 }}>{opt.label}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const ctrlBtnStyle: CSSProperties = {
  width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--pg-surface)',
  border: '1px solid var(--pg-border)',
  borderRadius: 4, cursor: 'pointer',
  color: 'var(--pg-text-secondary)',
  padding: 0,
}

const pickerItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '6px 8px',
  background: 'transparent',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  textAlign: 'left',
  color: 'var(--pg-text)',
  fontSize: '0.9125rem',
}
